import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useWeb3 } from '../contexts/Web3Context';
import { useGame } from '../contexts/GameContext';
import { WalletAdapterNetwork } from '@solana/wallet-adapter-base';

const ProfileContainer = styled.div`
  position: absolute;
  top: 20px;
  right: -400px;
  z-index: 100;
`;

const ProfileButton = styled.button`
  background: linear-gradient(135deg, var(--solana-primary), var(--solana-secondary));
  color: white;
  border: none;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
  overflow: hidden;
  position: relative;
  font-weight: bold;
  font-size: 16px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 14px rgba(0, 0, 0, 0.25);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, var(--solana-secondary), var(--solana-primary));
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::before {
    opacity: 1;
  }
  
  span {
    position: relative;
    z-index: 1;
  }
`;

const NotificationBadge = styled.div`
  position: absolute;
  top: -5px;
  right: -5px;
  width: 16px;
  height: 16px;
  background-color: #ff4757;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: white;
  font-weight: bold;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
  z-index: 2;
  animation: pulse 1.5s infinite;
  
  @keyframes pulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.2);
    }
    100% {
      transform: scale(1);
    }
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 55px;
  right: 0;
  background-color: var(--card-bg-color);
  border-radius: var(--border-radius);
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.3);
  width: 280px;
  padding: 20px;
  border: 1px solid #333;
  z-index: 10;
  animation: fadeIn 0.2s ease-in-out;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const ProfileHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid #333;
`;

const WalletAddress = styled.div`
  font-family: monospace;
  background-color: rgba(0, 0, 0, 0.2);
  padding: 8px 12px;
  border-radius: 5px;
  font-size: 14px;
  margin: 10px 0;
  color: var(--text-primary);
  width: 100%;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const InfoSection = styled.div`
  margin: 10px 0;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 10px 0;
  padding: 8px 0;
  border-bottom: 1px dashed #333;
  
  &:last-child {
    border-bottom: none;
  }
`;

const Label = styled.span`
  color: var(--text-secondary);
  font-size: 14px;
`;

const Value = styled.span`
  color: var(--text-primary);
  font-weight: 600;
  font-size: 16px;
`;

const ScoreValue = styled(Value)`
  color: var(--solana-secondary);
  font-size: 18px;
`;

const DisconnectButton = styled.button`
  background: linear-gradient(135deg, #ff5e62, #ff9966);
  color: white;
  border: none;
  border-radius: var(--border-radius);
  padding: 8px 16px;
  width: 100%;
  margin-top: 10px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
  
  &:hover {
    background: linear-gradient(135deg, #ff4d51, #ff8855);
    transform: translateY(-2px);
  }
`;

const Network = styled.div`
  background-color: #2b2b2b;
  border-radius: 12px;
  padding: 3px 10px;
  font-size: 12px;
  color: #14F195;
  display: inline-block;
  margin-top: 5px;
`;

const WelcomeMessage = styled.div`
  background-color: rgba(20, 241, 149, 0.1);
  border: 1px solid rgba(20, 241, 149, 0.3);
  border-radius: var(--border-radius);
  padding: 12px;
  margin: 10px 0;
  font-size: 14px;
  color: #14F195;
  line-height: 1.5;
  
  strong {
    display: block;
    margin-bottom: 5px;
  }
`;

const CloseWelcomeButton = styled.button`
  background: transparent;
  border: none;
  color: inherit;
  font-size: 12px;
  text-decoration: underline;
  cursor: pointer;
  margin-top: 5px;
  padding: 0;
  
  &:hover {
    color: white;
  }
`;

const ViewOnSolscanLink = styled.a`
  color: var(--solana-primary);
  font-size: 12px;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: rgba(153, 69, 255, 0.1);
  padding: 4px;
  transition: all 0.2s;
  
  &:hover {
    background-color: rgba(153, 69, 255, 0.2);
    transform: translateY(-2px);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ProfileDropdown: React.FC = () => {
  const { account, fullAddress, balance, disconnect, isConnected } = useWeb3();
  const { creditScore } = useGame();
  const [isOpen, setIsOpen] = useState(false);
  const [solscanSolBalance, setSolscanSolBalance] = useState<number | null>(null);
  const [tariffTokenBalance, setTariffTokenBalance] = useState("Soon");
  const [isLoading, setIsLoading] = useState(false);
  const [hasNewActivity, setHasNewActivity] = useState(false);
  const [previousBalance, setPreviousBalance] = useState<number | null>(null);
  const [showWelcome, setShowWelcome] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Solscan API Key
  const SOLSCAN_API_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjcmVhdGVkQXQiOjE3NDA0MjA5NTYxODEsImVtYWlsIjoibWFyaWFkZWhlaWRAZ21haWwuY29tIiwiYWN0aW9uIjoidG9rZW4tYXBpIiwiYXBpVmVyc2lvbiI6InYyIiwiaWF0IjoxNzQwNDIwOTU2fQ.u1NwHqI5ADEFsslPuaT0ETgVSoFlE36ca7mIGOJdkrY";

  // Check if user has seen welcome message
  useEffect(() => {
    const hasSeenWelcome = localStorage.getItem('hasSeenProfileWelcome');
    if (hasSeenWelcome) {
      setShowWelcome(false);
    }
  }, []);
  
  const dismissWelcome = () => {
    localStorage.setItem('hasSeenProfileWelcome', 'true');
    setShowWelcome(false);
  };

  // Check for balance changes
  useEffect(() => {
    if (previousBalance !== null && balance !== previousBalance) {
      setHasNewActivity(true);
    }
    setPreviousBalance(balance);
  }, [balance, previousBalance]);
  
  // Reset notification when dropdown is opened
  useEffect(() => {
    if (isOpen) {
      setHasNewActivity(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchSolscanData = async () => {
      if (fullAddress) {
        setIsLoading(true);
        try {
          // Fetch account details from Solscan Pro API
          const response = await fetch(`https://pro-api.solscan.io/v2.0/account/detail?account=${fullAddress}`, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'token': SOLSCAN_API_KEY  // Note: Using 'token' header instead of 'Authorization'
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            // Extract SOL balance from account details
            if (data && data.data && data.data.lamports) {
              const solBalance = parseFloat(data.data.lamports) / 1000000000;  // Converting lamports to SOL
              setSolscanSolBalance(solBalance);
              console.log('Solscan Sol Balance:', solBalance);
            }
            
            // Look for $TARIFF token in the tokens array (if it exists)
            if (data && data.data && data.data.tokens) {
              // For now, just set a default value, in a real app you'd look for a specific token
              // by its mint address and extract its balance
              setTariffTokenBalance("Soon");
            }
          } else {
            console.error('Solscan API error:', await response.text());
          }
        } catch (error) {
          console.error('Error fetching data from Solscan:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };
    
    if (isOpen && fullAddress) {
      fetchSolscanData();
    }
  }, [isOpen, fullAddress, SOLSCAN_API_KEY]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  if (!isConnected) return null;
  
  return (
    <ProfileContainer ref={dropdownRef}>
      <ProfileButton onClick={() => setIsOpen(!isOpen)}>
        <span>{account ? account.substring(0, 2) : ''}</span>
        {hasNewActivity && <NotificationBadge />}
      </ProfileButton>
      
      {isOpen && (
        <DropdownMenu>
          {showWelcome && (
            <WelcomeMessage>
              <strong>Welcome to your profile!</strong>
              This panel shows your wallet address, $TARIFF token balance, and your Tariff Score.
              <CloseWelcomeButton onClick={dismissWelcome}>
                Got it, don't show again
              </CloseWelcomeButton>
            </WelcomeMessage>
          )}
          
          <ProfileHeader>
            <h3>Wallet Profile</h3>
            <WalletAddress title={fullAddress || ''}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {account}
                {fullAddress && (
                  <ViewOnSolscanLink 
                    href={`https://solscan.io/account/${fullAddress}?cluster=devnet`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    title="View on Solscan"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                      <polyline points="15 3 21 3 21 9"></polyline>
                      <line x1="10" y1="14" x2="21" y2="3"></line>
                    </svg>
                  </ViewOnSolscanLink>
                )}
              </div>
            </WalletAddress>
          </ProfileHeader>
          
          <InfoSection>
            <InfoRow>
              <Label>$TARIFF Balance:</Label>
              <Value>
                {isLoading ? 'Loading...' : tariffTokenBalance}
              </Value>
            </InfoRow>
            
            <InfoRow>
              <Label>Tariff Score:</Label>
              <ScoreValue>{creditScore}</ScoreValue>
            </InfoRow>
          </InfoSection>
          
          <DisconnectButton onClick={() => {
            disconnect();
            setIsOpen(false);
          }}>
            Disconnect Wallet
          </DisconnectButton>
        </DropdownMenu>
      )}
    </ProfileContainer>
  );
};

export default ProfileDropdown; 